import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const CHART_HEIGHT = 220;
/** Matches AdminStats.tsx's PALETTE[0]. Hardcoded hex per
 * docs/design-guidelines.md §Charts, which forbids the chart-1..5 tokens. */
const BAR_FILL = "#059669";

interface PortfolioChartsProps {
  byCountry: Array<{ country: string; count: number }>;
  byStage: Array<{ stageId: string; stageName: string; count: number }>;
}

function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
      <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-slate-200">
        <p className="px-3 text-center text-xs text-slate-400">{message}</p>
      </div>
    </div>
  );
}

/**
 * Both charts are single-color on purpose. stage-accent.ts can't be used: it
 * exports Tailwind `bg-*` class strings, which set background-color, not the
 * SVG `fill` a recharts <Cell> needs. Mirroring it as a parallel hex array
 * would create a second source of truth for stage color; the axis label
 * already carries everything the color would.
 */
function ChartPanel({
  title,
  emptyMessage,
  data,
  categoryKey,
}: {
  title: string;
  emptyMessage: string;
  data: Array<Record<string, unknown>>;
  categoryKey: string;
}) {
  return (
    <section className="flex-1 rounded-xl border border-border bg-card p-6 shadow-sm">
      <h3 className="text-base font-bold leading-6">{title}</h3>
      {data.length === 0 ? (
        <EmptyChartState message={emptyMessage} />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 12, bottom: 0 }}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey={categoryKey} width={120} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="count" fill={BAR_FILL} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </section>
  );
}

export function PortfolioCharts({ byCountry, byStage }: PortfolioChartsProps) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <ChartPanel
        title="Assets by country"
        emptyMessage="No assets yet"
        data={byCountry}
        categoryKey="country"
      />
      <ChartPanel
        title="Assets by value chain stage"
        emptyMessage="No stages mapped yet"
        data={byStage}
        categoryKey="stageName"
      />
    </div>
  );
}
